import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import Swal from "sweetalert2";
import moment from "moment";
import withRouter from "../../withRouter.js";
import Footer from "../../Component/Footer";
import Header from "../../Component/Header";
import Menu from "../../Component/Menu";
import { clientname, currentTenant, logo, userid } from "../../Common/Credentials";
import { spin } from "../../Common/NewLoader";
import { productionid } from "../../Common/Permissions";
import ProductionServices from "../ProductionServices";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const QATable = (props) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const navigate = useNavigate();

  // Search highlighting methods
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightSearchText = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
    const parts = String(text).split(regex);

    return parts.map((part, index) =>
      regex.test(part)
        ? React.createElement('mark', {
          key: index,
          className: 'UI-search-highlight'
        }, part)
        : part
    );
  };

  // Handle resize
  const handleResize = () => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    if (!mobile) {
      setShowSearch(false);
    }
  };

  const refreshData = useCallback(() => {
    setLoading(true);
    ProductionServices.GetallQA(currentTenant)
      .then((response) => {
        const sortedData = response.data.sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        );
        setData(sortedData);
        setFilteredData(sortedData);
        setLoading(false);
      })
      .catch((e) => {
        if (e.response?.data?.error === "Unauthorized") {
          Swal.fire({
            title: "Your Session is Lost: Please login Back to resume",
            text: ".",
            icon: "error",
            confirmButtonText: "Ok",
            confirmButtonColor: "#152f5f",
          }).then(() => navigate("/"));
        }
        setLoading(false);
      });
  }, [currentTenant, navigate]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setShowSearch(true);
    }
    window.addEventListener('resize', handleResize);

    if (!userid) {
      window.confirm("Your Session is Lost: Please login Back to resume");
      navigate("/");
      return;
    }

    refreshData();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [navigate, refreshData]);

  useEffect(() => {
    updateFilteredData();
  }, [data, searchTerm]);

  // Sorting function
  const sortData = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key] || '';
      let bValue = b[sortConfig.key] || '';

      // Handle date sorting
      if (sortConfig.key === 'createdAt') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const updateFilteredData = () => {
    let filtered;
    if (!searchTerm.trim()) {
      filtered = data;
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = data.filter(item =>
        item.id?.toString().toLowerCase().includes(lowerSearchTerm) ||
        item.joid?.toString().toLowerCase().includes(lowerSearchTerm) ||
        item.createdby?.toLowerCase().includes(lowerSearchTerm) ||
        moment(item.createdAt).format('DD-MM-YYYY HH:mm').toLowerCase().includes(lowerSearchTerm)
      );
    }
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  // Get current page data for pagination
  const getCurrentPageData = () => {
    const sortedData = getSortedData(filteredData);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedData.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const generatePDF = () => {
    const pdfData = filteredData.map(item => ({
      ...item,
      createdAt: moment(item.createdAt).format("DD-MM-YYYY HH:mm"),
      qaId: item.id,
      jobOrderId: item.joid
    }));

    const columns = [
      { header: "QA ID", dataKey: "qaId" },
      { header: "Job Order ID", dataKey: "jobOrderId" },
      { header: "Created By", dataKey: "createdby" },
      { header: "Date", dataKey: "createdAt" },
    ];

    const doc = new jsPDF("p", "pt");
    
    // Add logo
    if (logo) {
      doc.addImage(logo, "GIF", 40, 20, 60, 60);
    }
    
    // Add company name
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(`${clientname}`, 180, 40);
    
    // Add report title
    doc.setFontSize(14);
    doc.setFont("helvetica", "normal");
    doc.text(`Quality Analysis Report`, 180, 80);
    
    // Add timestamp
    doc.setFontSize(10);
    doc.text(`${moment().format("DD-MM-YYYY HH:mm")}`, 500, 80);
    
    // Add line separator
    doc.line(40, 90, 555, 90);
    
    // Add table
    autoTable(doc, {
      theme: "grid",
      startY: 100,
      margin: { left: 40, right: 40 },
      headStyles: { fillColor: [21, 47, 95] },
      body: pdfData,
      columns: columns,
      styles: { fontSize: 9 },
      headStyles: { fontSize: 10, fontStyle: 'bold' }
    });
    
    // Save the PDF
    doc.save(`Quality_Analysis_Report_${moment().format('DDMMYYYY_HHmm')}.pdf`);
  };

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fa fa-sort UI-sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fa fa-sort-up UI-sort-icon UI-sort-active" />
      : <i className="fa fa-sort-down UI-sort-icon UI-sort-active" />;
  };

  // Render pagination controls
  const renderPagination = () => {
    if (filteredData.length <= itemsPerPage) return null;

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    return (
      <div className={`UI-pagination-container ${isMobile ? 'UI-pagination-container-mobile' : ''}`}>
        <div className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} entries
        </div>
        <div className={`UI-pagination-controls ${isMobile ? 'UI-pagination-controls-mobile' : ''}`}>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className={`UI-page-size-select ${isMobile ? 'UI-page-size-select-mobile' : ''}`}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'first' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('first')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'First' : 'First'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'prev' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('prev')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Prev' : 'Previous'}
          </button>

          <span className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'next' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('next')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Next' : 'Next'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'last' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('last')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Last' : 'Last'}
          </button>
        </div>
      </div>
    );
  };

  const renderTable = () => {
    const currentPageData = getCurrentPageData();

    if (filteredData.length === 0) {
      return (
        <div className={`UI-no-data-message ${isMobile ? 'UI-no-data-message-mobile' : ''}`}>
          {searchTerm ? 'No quality analysis records found matching your search' : 'No quality analysis records available'}
        </div>
      );
    }

    return (
      <>
        <div className={`UI-table-container ${isMobile ? 'UI-table-container-mobile' : ''}`}>
          <table className={`UI-table ${isMobile ? 'UI-table-mobile' : ''}`}>
            <thead className="UI-table-header">
              <tr>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'id' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('id')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    QA ID
                    <SortIndicator columnKey="id" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'joid' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('joid')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Job Order ID
                    <SortIndicator columnKey="joid" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'createdby' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('createdby')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Created By
                    <SortIndicator columnKey="createdby" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'createdAt' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('createdAt')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Date
                    <SortIndicator columnKey="createdAt" />
                  </div>
                </th>
                <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} UI-th-actions`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentPageData.map((item) => (
                <tr
                  key={item.id}
                  className={`UI-table-row ${hoveredRow === item.id ? 'UI-table-row-hovered' : ''} ${isMobile ? 'UI-table-row-mobile' : ''}`}
                  onMouseEnter={() => setHoveredRow(item.id)}
                  onMouseLeave={() => {
                    setHoveredRow(null);
                    setHoveredAction(null);
                  }}
                >
                  {/* QA ID Cell */}
                  <td
                    className={`UI-td UI-service-name ${hoveredRow === item.id ? 'UI-service-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(item.id, searchTerm)}
                  </td>

                  {/* Job Order ID Cell */}
                  <td
                    className={`UI-td ${hoveredRow === item.id ? 'UI-item-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    <Link
                      to="/joborder"
                      state={item.joid}
                      className="UI-joborder-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {highlightSearchText(item.joid, searchTerm)}
                    </Link>
                  </td>

                  {/* Created By Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(item.createdby, searchTerm)}
                  </td>

                  {/* Date Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {moment(item.createdAt).format('DD-MM-YYYY HH:mm')}
                  </td>

                  {/* Actions Cell */}
                  <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                    <div className="UI-action-buttons">
                      {/* View QA Details */}
                      <Tooltip title="View Quality Analysis Details" arrow>
                        <Link
                          to="/qadetails"
                          state={item}
                          className={`UI-action-button ${hoveredAction === `view-${item.id}` ? 'UI-view-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                          onMouseEnter={() => setHoveredAction(`view-${item.id}`)}
                          onMouseLeave={() => setHoveredAction(null)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <i className={`fa fa-list-alt UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `view-${item.id}` ? 'UI-action-icon-hover' : ''}`} />
                        </Link>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination()}
      </>
    );
  };

  return (
    <div>
      <Header />
      <Menu />
      <div className="content-wrapper">
        <div className={`UI-container ${isMobile ? 'UI-container-mobile' : ''}`}>
          <div className="UI-card">
            {/* Header */}
            <div className={`UI-header ${isMobile ? 'UI-header-mobile' : ''}`}>
              <nav className={`UI-breadcrumb ${isMobile ? 'UI-breadcrumb-mobile' : ''}`}>
                <Link
                  to="/production"
                  className={`UI-breadcrumb-item ${hovered === 'home' ? 'UI-breadcrumb-item-hover' : ''}`}
                  onMouseEnter={() => setHovered('home')}
                  onMouseLeave={() => setHovered(null)}
                >
                  Home
                </Link>
                <span>/</span>
                <span className="UI-breadcrumb-item UI-breadcrumb-active">
                  Quality Analysis
                </span>
              </nav>

              <div className={`UI-header-content ${isMobile ? 'UI-header-content-mobile' : ''}`}>
                <div>
                  <h1 className={`UI-title ${isMobile ? 'UI-title-mobile' : ''}`}>
                    Quality Analysis
                  </h1>
                  <p className={`UI-subtitle ${isMobile ? 'UI-subtitle-mobile' : ''}`}>
                    Manage quality analysis records
                  </p>
                </div>

                <div className={`UI-header-actions ${isMobile ? 'UI-header-actions-mobile' : ''}`}>
                  {/* Mobile Search */}
                  {isMobile && (
                    <input
                      type="text"
                      placeholder="Search QA records..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="UI-mobile-search-input"
                    />
                  )}

                  <div className={`UI-buttons-row ${isMobile ? 'UI-buttons-row-mobile' : ''}`}>
                    {/* Desktop Search Toggle */}
                    {!isMobile && (
                      <div
                        className={`UI-search-container ${showSearch ? 'UI-search-container-expanded' : ''} ${hovered === "search" ? 'UI-search-container-hover' : ''}`}
                        onClick={() => {
                          if (!showSearch) setShowSearch(true);
                        }}
                        onMouseEnter={() => setHovered("search")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i
                          className="fa fa-search UI-search-icon"
                        />
                        {!showSearch && "Search"}
                        {showSearch && (
                          <input
                            type="text"
                            placeholder="Search QA records..."
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="UI-search-input"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            onBlur={() => {
                              if (searchTerm.trim() === "") {
                                setShowSearch(false);
                              }
                            }}
                          />
                        )}
                      </div>
                    )}

                    {/* Export PDF Button */}
                    <button
                      className={`UI-btn UI-btn-secondary ${hovered === "pdf" ? 'UI-btn-secondary-hover' : ''} ${isMobile ? 'UI-btn-mobile' : ''}`}
                      onClick={generatePDF}
                      onMouseEnter={() => setHovered("pdf")}
                      onMouseLeave={() => setHovered(null)}
                    >
                      <i className="fa fa-file-pdf" /> {isMobile ? "PDF" : "Export"}
                    </button>

                    {/* New QA button - Only show if user has permission */}
                    {(productionid === '2' || productionid === '3') && (
                      <Link
                        to="/newqa"
                        state={{
                          id: -1,
                          pagename: "New InProcess",
                          salestype: false,
                        }}
                        className={`UI-btn UI-btn-primary ${hovered === "new" ? 'UI-btn-primary-hover' : ''} ${isMobile ? 'UI-btn-mobile' : ''}`}
                        onMouseEnter={() => setHovered("new")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i className="fa fa-plus" /> {isMobile ? "New" : "New QA"}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="UI-loading-container">
                {spin}
              </div>
            ) : (
              renderTable()
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default withRouter(QATable);